import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Alert } from '../components/ui/alert';
import { Label } from '../components/ui/label';
import { ArrowLeft, Mail, Send } from 'lucide-react';

const ForgotPassword: React.FC = () => {
  const { t } = useLanguage();
  const { requestPasswordReset, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/documents', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email.trim()) {
      setError(t('emailRequired'));
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError(t('invalidEmailFormat'));
      return;
    }

    setIsLoading(true);
    try {
      await requestPasswordReset(email.trim());
      setSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('requestPasswordResetFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="mx-auto mt-10 w-full max-w-md">
      <Card>
        <CardHeader>
          <CardTitle className="text-center text-3xl">{t('recoverPasswordTitle')}</CardTitle>
          <CardDescription className="text-center">{t('recoverPasswordDescription')}</CardDescription>
        </CardHeader>
        <CardContent>
          {sent ? (
            <div className="space-y-4">
              <Alert variant="success">{t('resetEmailSent')}</Alert>
              <Button variant="outline" className="w-full" asChild>
                <RouterLink to="/login">
                  <ArrowLeft className="h-4 w-4" />
                  {t('backToSignIn')}
                </RouterLink>
              </Button>
            </div>
          ) : (
            <>
              {error && (
                <Alert variant="destructive" className="mb-4">
                  {error}
                </Alert>
              )}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-1">
                  <Label htmlFor="forgot-email">{t('emailAddress')}</Label>
                  <div className="relative">
                    <Mail className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="forgot-email"
                      type="email"
                      autoComplete="email"
                      autoFocus
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                </div>
                <Button type="submit" className="w-full" disabled={isLoading}>
                  <Send className="h-4 w-4" />
                  {isLoading ? t('sendingResetLink') : t('sendResetLink')}
                </Button>
                <p className="text-center text-sm text-muted-foreground">
                  <RouterLink to="/login" className="inline-flex items-center justify-center gap-1 text-primary hover:underline">
                    <ArrowLeft className="h-3 w-3" />
                    {t('backToSignIn')}
                  </RouterLink>
                </p>
              </form>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ForgotPassword;
